import { useState, useEffect, useRef, useCallback } from "react";
import { apiGet, apiGetBlob, apiPost, fmtCurrency, fmtDate } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCcw, Loader2, AlertTriangle, FileSpreadsheet, Search, X, Clock, SearchCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const BUCKET_COLUMNS = [
  { key: "material_value", label: "Material Value" },
  { key: "freight", label: "Freight" },
  { key: "loading", label: "Loading" },
  { key: "unloading", label: "Unloading" },
  { key: "packing", label: "Packing" },
  { key: "other_charges", label: "Other Charges" },
  { key: "round_off", label: "Round Off" },
];

const fmtTimestamp = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const TablePager = ({ page, setPage, total, pageSize, testId }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  return (
    <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between gap-2" data-testid={testId}>
      <span className="text-[11px] text-slate-400 font-mono">Showing {from}-{to} of {total}</span>
      <div className="flex items-center gap-1">
        <Button
          size="sm" variant="outline" disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="h-7 w-7 p-0 border-slate-300" data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={13} />
        </Button>
        <span className="text-[11px] text-slate-500 font-mono px-1.5">{page + 1} / {pageCount}</span>
        <Button
          size="sm" variant="outline" disabled={page >= pageCount - 1}
          onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          className="h-7 w-7 p-0 border-slate-300" data-testid={`${testId}-next`}
        >
          <ChevronRight size={13} />
        </Button>
      </div>
    </div>
  );
};

// One Supplier Summary table + one Per-Invoice Detail table, scoped to a
// single supplier_type ("product" or "logistics"). Rendered once per tab
// (see <TabsContent> below) with the exact same table markup that used to
// render once for everything -- just pre-filtered data and a distinct
// `idPrefix` for data-testids.
function SupplierTypeSection({
  idPrefix, supplierRows, pagedSupplierRows, supplierPage, setSupplierPage,
  invoiceRows, pagedInvoiceRows, invoicePage, setInvoicePage,
  vendorFilter, setVendorFilter, vendorOptions,
  searchText, setSearchText, dateFrom, setDateFrom, dateTo, setDateTo,
  hasActiveFilters, clearFilters, extraChargesExport,
}) {
  return (
    <div className="space-y-4">
      {/* Supplier-level summary */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
          <h3 className="text-[13px] font-heading font-semibold text-slate-800">Supplier Summary</h3>
          {extraChargesExport && (
            <Button
              size="sm"
              variant="outline"
              onClick={extraChargesExport.onExport}
              disabled={extraChargesExport.exporting}
              className="h-7 gap-1.5 text-[11px] ml-auto border-slate-300"
              data-testid="export-extra-charges-button"
            >
              {extraChargesExport.exporting ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />}
              Export suppliers with extra charges
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid={`${idPrefix}-supplier-summary-table`}>
            <thead>
              <tr className="bg-slate-200 border-b-2 border-slate-300">
                {["Supplier", "Invoices", ...BUCKET_COLUMNS.map((c) => c.label), "Total Charges", "Charges % of Material", "Charge Types Used"].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left font-bold text-slate-700 uppercase text-[10px] tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-300">
              {!supplierRows.length ? (
                <tr><td colSpan={11} className="py-10 text-center text-slate-400">No posted invoices found in this period</td></tr>
              ) : pagedSupplierRows.map((s) => (
                <tr
                  key={`${s.vendor_id}|${s.currency}`}
                  className={`hover:bg-slate-50 cursor-pointer ${vendorFilter === s.vendor_id ? "bg-[#0A6755]/5" : ""}`}
                  onClick={() => setVendorFilter(vendorFilter === s.vendor_id ? "all" : s.vendor_id)}
                  data-testid={`${idPrefix}-supplier-summary-row-${s.vendor_id}-${s.currency}`}
                >
                  <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                    {s.vendor_name} <span className="text-slate-400 font-mono text-[10px]">({s.vendor_id})</span>
                    {s.currency !== "INR" && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-mono font-semibold" data-testid={`currency-tag-${s.vendor_id}-${s.currency}`}>
                        {s.currency}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-slate-500">{s.invoice_count}</td>
                  {BUCKET_COLUMNS.map((c) => (
                    <td key={c.key} className="px-3 py-2.5 font-mono text-slate-700 whitespace-nowrap">{fmtCurrency(s[c.key], s.currency)}</td>
                  ))}
                  <td className="px-3 py-2.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtCurrency(s.total_charges, s.currency)}</td>
                  <td className="px-3 py-2.5 font-mono text-slate-700">{s.charges_pct_of_material != null ? `${s.charges_pct_of_material}%` : "—"}</td>
                  <td className="px-3 py-2.5 text-slate-500 text-[11px]">{s.charge_types_used?.map((b) => BUCKET_COLUMNS.find((c) => c.key === b)?.label || b).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager page={supplierPage} setPage={setSupplierPage} total={supplierRows.length} pageSize={PAGE_SIZE} testId={`${idPrefix}-supplier-summary-pager`} />
      </div>

      {/* Per-invoice detail */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap">
          <h3 className="text-[13px] font-heading font-semibold text-slate-800">Per-Invoice Detail</h3>
          <div className="relative ml-auto">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search invoice no., SAP ID, supplier…"
              className="w-64 h-7 pl-7 text-[12px] border-slate-300"
              data-testid={`${idPrefix}-invoice-search-input`}
            />
          </div>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-36 h-7 text-[12px] border-slate-300"
            data-testid={`${idPrefix}-date-from-input`}
            aria-label="From date"
          />
          <span className="text-slate-400 text-[11px]">to</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-36 h-7 text-[12px] border-slate-300"
            data-testid={`${idPrefix}-date-to-input`}
            aria-label="To date"
          />
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="w-56 h-7 text-[12px] border-slate-300" data-testid={`${idPrefix}-vendor-filter-select`}>
              <SelectValue placeholder="All suppliers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All suppliers</SelectItem>
              {vendorOptions.map(([vendorId, vendorName]) => (
                <SelectItem key={vendorId} value={vendorId}>{vendorName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              className="h-7 gap-1 text-[12px] text-slate-500 px-2"
              data-testid={`${idPrefix}-clear-filters-button`}
            >
              <X size={12} /> Clear
            </Button>
          )}
          <span className="text-[11px] text-slate-400 font-mono w-full text-right sm:w-auto">{invoiceRows.length} invoice{invoiceRows.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]" data-testid={`${idPrefix}-per-invoice-table`}>
            <thead>
              <tr className="bg-slate-200 border-b-2 border-slate-300">
                {["SAP Doc ID", "Supplier", "Invoice Date", ...BUCKET_COLUMNS.map((c) => c.label)].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left font-bold text-slate-700 uppercase text-[10px] tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-300">
              {!invoiceRows.length ? (
                <tr><td colSpan={10} className="py-10 text-center text-slate-400">No invoices to show</td></tr>
              ) : pagedInvoiceRows.map((r) => (
                <tr key={r.sap_invoice_id} className="hover:bg-slate-50" data-testid={`${idPrefix}-invoice-row-${r.sap_invoice_id}`} title={r.warning || undefined}>
                  <td className="px-3 py-2.5 font-mono text-[#0052CC] whitespace-nowrap">{r.sap_invoice_id}{r.warning ? " ⚠" : ""}</td>
                  <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                    {r.vendor_name}
                    {r.currency !== "INR" && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-mono font-semibold" data-testid={`invoice-currency-tag-${r.sap_invoice_id}`}>
                        {r.currency}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-slate-500 whitespace-nowrap">{fmtDate(r.invoice_date)}</td>
                  {BUCKET_COLUMNS.map((c) => (
                    <td key={c.key} className="px-3 py-2.5 font-mono text-slate-700 whitespace-nowrap">{fmtCurrency(r[c.key], r.currency)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager page={invoicePage} setPage={setInvoicePage} total={invoiceRows.length} pageSize={PAGE_SIZE} testId={`${idPrefix}-per-invoice-pager`} />
      </div>
    </div>
  );
}

export default function SupplierChargesReport() {
  const [view, setView]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [vendorFilter, setVendorFilter] = useState("all");
  const [activeTypeTab, setActiveTypeTab] = useState("product");
  const [searchText, setSearchText]   = useState("");
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [lookupId, setLookupId]       = useState("");
  const [lookupBusy, setLookupBusy]   = useState(false);
  const [invoicePage, setInvoicePage] = useState(0);
  const [supplierPage, setSupplierPage] = useState(0);
  const pollRef = useRef(null);
  const lookupTimersRef = useRef([]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const clearLookupTimers = () => { lookupTimersRef.current.forEach(clearTimeout); lookupTimersRef.current = []; };

  const pollLatest = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const data = await apiGet("/reports/supplier-charges/latest");
        setView(data);
        if (data.scan?.status !== "running") { stopPolling(); setRefreshing(false); }
      } catch (e) { stopPolling(); setRefreshing(false); toast.error(e.message); }
    }, 3000);
  }, []);

  const loadLatest = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await apiGet("/reports/supplier-charges/latest");
      setView(data);
      if (data.scan?.status === "running") { setRefreshing(true); pollLatest(); }
    } catch (e) { if (!silent) toast.error(e.message); }
    finally { if (!silent) setLoading(false); }
  }, [pollLatest]);

  useEffect(() => { loadLatest(); return () => { stopPolling(); clearLookupTimers(); }; }, [loadLatest]);

  const refreshFromSAP = async () => {
    setRefreshing(true);
    try {
      await apiPost("/reports/supplier-charges/generate", {});
      pollLatest();
    } catch (e) { setRefreshing(false); toast.error(e.message); }
  };

  const lookupSupplier = async () => {
    const vendorId = lookupId.trim();
    if (!vendorId) return;
    setLookupBusy(true);
    try {
      const fyQuery = view?.fy_start && view?.fy_end ? `&fy_start=${view.fy_start}&fy_end=${view.fy_end}` : "";
      await apiPost(`/reports/supplier-charges/refresh-supplier?vendor_id=${encodeURIComponent(vendorId)}${fyQuery}`, {});
      toast.success(`Pulling ${vendorId}'s SAP ledger for this FY in the background — this view will update automatically over the next few minutes.`);
      setLookupId("");
      clearLookupTimers();
      // A few delayed, SILENT refetches pick up the scoped scan's results
      // once they land in the cache -- a scoped pull can take minutes
      // (not seconds) while a full company-wide scan shares the same
      // rate-limited SAP read pool, so this spreads out further than a
      // quick poll would. Silent = no loading spinner replacing the
      // already-visible tables mid-refetch.
      [20000, 45000, 90000, 150000, 220000].forEach((delay) => {
        lookupTimersRef.current.push(setTimeout(() => loadLatest({ silent: true }), delay));
      });
    } catch (e) { toast.error(e.message); }
    finally { setLookupBusy(false); }
  };

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const blob = await apiGetBlob("/reports/supplier-charges/export");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Supplier_Charges_Report_${view?.fy_label || "export"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setExporting(false); }
  };

  const [exportingExtraCharges, setExportingExtraCharges] = useState(false);
  const exportExtraChargesExcel = async () => {
    setExportingExtraCharges(true);
    try {
      const blob = await apiGetBlob("/reports/supplier-charges/export-extra-charges");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Product_Suppliers_Extra_Charges_${view?.fy_label || "export"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
    finally { setExportingExtraCharges(false); }
  };

  const perInvoice = view?.per_invoice || [];
  const supplierSummary = view?.supplier_summary || [];
  const typeSummary = view?.type_summary || [];
  const scan = view?.scan;
  const search = searchText.trim().toLowerCase();
  // Logistics and Product suppliers are rendered as two fully separate
  // tables (one <Tabs> panel each, see SupplierTypeSection) instead of one
  // combined table with a type filter -- vendor/search/date filters still
  // apply within whichever tab is active.
  const filteredInvoices = perInvoice.filter((r) => {
    if (vendorFilter !== "all" && r.vendor_id !== vendorFilter) return false;
    if (search && !(`${r.invoice_number || ""} ${r.sap_invoice_id || ""} ${r.vendor_name || ""}`.toLowerCase().includes(search))) return false;
    if (dateFrom && (!r.invoice_date || r.invoice_date < dateFrom)) return false;
    if (dateTo && (!r.invoice_date || r.invoice_date > dateTo)) return false;
    return true;
  });
  const productSupplierSummary = supplierSummary.filter((s) => s.supplier_type !== "logistics");
  const logisticsSupplierSummary = supplierSummary.filter((s) => s.supplier_type === "logistics");
  const productInvoices = filteredInvoices.filter((r) => r.supplier_type !== "logistics");
  const logisticsInvoices = filteredInvoices.filter((r) => r.supplier_type === "logistics");
  const hasActiveFilters = vendorFilter !== "all" || !!search || !!dateFrom || !!dateTo;
  const clearFilters = () => { setVendorFilter("all"); setSearchText(""); setDateFrom(""); setDateTo(""); };
  // Rendering the full filtered list as table rows (growing toward the
  // ~18,800-document SAP universe this report now covers) made the page
  // take ~18s to paint on a 5,800-row cache (live-caught Feb 2026) --
  // slice to one page of rows for the DOM, resetting to page 1 whenever a
  // filter (or the active Logistics/Product tab) narrows/widens the
  // result set so the user never lands on a now out-of-range page.
  useEffect(() => { setInvoicePage(0); }, [vendorFilter, search, dateFrom, dateTo, activeTypeTab]);
  useEffect(() => {
    setSupplierPage(0); setVendorFilter("all"); setSearchText(""); setDateFrom(""); setDateTo("");
  }, [activeTypeTab]);
  const pagedProductSupplierSummary = productSupplierSummary.slice(supplierPage * PAGE_SIZE, (supplierPage + 1) * PAGE_SIZE);
  const pagedLogisticsSupplierSummary = logisticsSupplierSummary.slice(supplierPage * PAGE_SIZE, (supplierPage + 1) * PAGE_SIZE);
  const pagedProductInvoices = productInvoices.slice(invoicePage * PAGE_SIZE, (invoicePage + 1) * PAGE_SIZE);
  const pagedLogisticsInvoices = logisticsInvoices.slice(invoicePage * PAGE_SIZE, (invoicePage + 1) * PAGE_SIZE);
  // A supplier can have more than one supplier_summary row (one per
  // currency it invoices in -- see backend _summarize_by_supplier), but the
  // filter itself is still per vendor_id, so dedupe for the dropdown.
  const vendorOptionsFor = (rows) => Array.from(new Map(rows.map((s) => [s.vendor_id, s.vendor_name])).entries());
  const productVendorOptions = vendorOptionsFor(productSupplierSummary);
  const logisticsVendorOptions = vendorOptionsFor(logisticsSupplierSummary);
  const progress = scan?.progress || { headers_total: 0, items_read: 0, items_skipped: 0, items_failed: 0 };
  const progressDone = progress.items_read + progress.items_skipped + progress.items_failed;

  return (
    <div className="space-y-4">
      {/* Header / controls */}
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
        <div>
          <p className="text-[11px] font-mono uppercase text-slate-400 tracking-wide">
            {view?.fy_label || "Current Financial Year"}
          </p>
          <p className="text-[12px] text-slate-500">
            Material value vs. supplementary charges, sourced from a SAP-synced cache, all amounts pre-tax.
          </p>
          {view?.cache_as_of && (
            <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5" data-testid="cache-as-of-label">
              <Clock size={11} /> Data as of {fmtTimestamp(view.cache_as_of)}
            </p>
          )}
        </div>
        {!!perInvoice.length && (
          <Button
            size="sm"
            variant="outline"
            onClick={exportExcel}
            disabled={exporting}
            className="h-8 gap-1.5 text-[12px] ml-auto border-slate-300"
            data-testid="export-excel-button"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <FileSpreadsheet size={13} />}
            Export to Excel
          </Button>
        )}
        <Button
          size="sm"
          onClick={refreshFromSAP}
          disabled={refreshing}
          className={`h-8 gap-1.5 text-[12px] bg-[#0A6755] hover:bg-[#0A6755]/90 ${perInvoice.length ? "" : "ml-auto"}`}
          data-testid="refresh-from-sap-button"
        >
          {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />}
          {refreshing ? "Scanning SAP…" : "Refresh from SAP"}
        </Button>
      </div>

      {/* Scoped single-supplier lookup */}
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-2 flex-wrap">
        <SearchCheck size={14} className="text-slate-400 shrink-0" />
        <span className="text-[12px] text-slate-500 shrink-0">Need one supplier right now instead of waiting on a full refresh?</span>
        <Input
          value={lookupId}
          onChange={(e) => setLookupId(e.target.value)}
          placeholder="Vendor ID, e.g. M1534"
          className="w-44 h-7 text-[12px] border-slate-300"
          data-testid="supplier-lookup-input"
          onKeyDown={(e) => { if (e.key === "Enter") lookupSupplier(); }}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={lookupSupplier}
          disabled={lookupBusy || !lookupId.trim()}
          className="h-7 gap-1.5 text-[12px] border-slate-300"
          data-testid="supplier-lookup-button"
        >
          {lookupBusy ? <Loader2 size={12} className="animate-spin" /> : null}
          Pull This Supplier's SAP Ledger
        </Button>
      </div>

      {refreshing && (
        <div className="bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid="report-progress">
          <div className="flex items-center justify-between text-[12px] text-slate-500 mb-1.5">
            <span>Scanning SAP for every posted invoice this financial year… (existing data below stays visible)</span>
            <span className="font-mono">{progressDone} / {progress.headers_total || "…"}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0A6755] transition-all"
              style={{ width: progress.headers_total ? `${(progressDone / progress.headers_total) * 100}%` : "4%" }}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><Loader2 size={20} className="animate-spin mx-auto text-slate-400" /></div>
      ) : scan?.status === "error" && !perInvoice.length ? (
        <div className="bg-red-50 border border-red-200 rounded-lg py-10 text-center text-[13px] text-red-700 flex flex-col items-center gap-2" data-testid="report-error-state">
          <AlertTriangle size={18} />
          <span>{scan.error_reason || "The last SAP scan did not finish. Click \"Refresh from SAP\" to try again."}</span>
        </div>
      ) : !perInvoice.length ? (
        <div className="bg-white border border-slate-200 rounded-lg py-16 text-center text-[13px] text-slate-400" data-testid="no-report-state">
          No cached data yet for this financial year. Click "Refresh from SAP" to scan every posted invoice (first scan can take 1-2 hours; later ones are fast), or pull a single supplier above.
        </div>
      ) : (
        <>
          {!!view?.errors?.length && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-start gap-2 text-[12px] text-amber-800" data-testid="report-errors-banner">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{view.errors.length} invoice(s) could not be read live from SAP during the last scan and are excluded from the totals below.</span>
            </div>
          )}

          {/* Grand totals split by supplier type -- click a card to jump to that tab below */}
          {!!typeSummary.length && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="type-summary-section">
              {typeSummary.map((t) => (
                <button
                  key={`${t.supplier_type}|${t.currency}`}
                  onClick={() => setActiveTypeTab(t.supplier_type)}
                  className={`text-left bg-white border rounded-lg px-4 py-3 transition-colors ${
                    activeTypeTab === t.supplier_type ? "border-[#0A6755] ring-1 ring-[#0A6755]/30" : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`type-summary-card-${t.supplier_type}-${t.currency}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[12px] font-heading font-semibold text-slate-800">
                      {t.supplier_type === "logistics" ? "Logistics Charges" : "Product Purchases"}
                      {t.currency !== "INR" && <span className="ml-1.5 text-[10px] font-mono text-amber-700">({t.currency})</span>}
                    </span>
                    <span className="text-[11px] text-slate-400 font-mono">{t.invoice_count} invoice{t.invoice_count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-baseline gap-3 text-[13px]">
                    <span className="font-mono font-bold text-slate-900">{fmtCurrency(t.grand_total, t.currency)}</span>
                    <span className="text-[11px] text-slate-400">
                      Material {fmtCurrency(t.material_value, t.currency)} + Charges {fmtCurrency(t.total_charges, t.currency)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Logistics vs. Product -- two fully separate sets of tables */}
          <Tabs value={activeTypeTab} onValueChange={setActiveTypeTab}>
            <TabsList data-testid="supplier-type-tabs">
              <TabsTrigger value="product" data-testid="tab-product-suppliers">
                Product Suppliers <span className="ml-1.5 text-slate-400 font-mono">{productSupplierSummary.length}</span>
              </TabsTrigger>
              <TabsTrigger value="logistics" data-testid="tab-logistics-suppliers">
                Logistics Suppliers <span className="ml-1.5 text-slate-400 font-mono">{logisticsSupplierSummary.length}</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="product">
              <SupplierTypeSection
                idPrefix="product"
                supplierRows={productSupplierSummary} pagedSupplierRows={pagedProductSupplierSummary}
                supplierPage={supplierPage} setSupplierPage={setSupplierPage}
                invoiceRows={productInvoices} pagedInvoiceRows={pagedProductInvoices}
                invoicePage={invoicePage} setInvoicePage={setInvoicePage}
                vendorFilter={vendorFilter} setVendorFilter={setVendorFilter} vendorOptions={productVendorOptions}
                searchText={searchText} setSearchText={setSearchText}
                dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
                hasActiveFilters={hasActiveFilters} clearFilters={clearFilters}
                extraChargesExport={{ onExport: exportExtraChargesExcel, exporting: exportingExtraCharges }}
              />
            </TabsContent>
            <TabsContent value="logistics">
              <SupplierTypeSection
                idPrefix="logistics"
                supplierRows={logisticsSupplierSummary} pagedSupplierRows={pagedLogisticsSupplierSummary}
                supplierPage={supplierPage} setSupplierPage={setSupplierPage}
                invoiceRows={logisticsInvoices} pagedInvoiceRows={pagedLogisticsInvoices}
                invoicePage={invoicePage} setInvoicePage={setInvoicePage}
                vendorFilter={vendorFilter} setVendorFilter={setVendorFilter} vendorOptions={logisticsVendorOptions}
                searchText={searchText} setSearchText={setSearchText}
                dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo}
                hasActiveFilters={hasActiveFilters} clearFilters={clearFilters}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
